import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Loader2, Play } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Cover } from "@/components/Cover";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { usePlayer } from "@/context/PlayerContext";
import { api } from "@/lib/api";

const RANGES = { "7d": "7 days", "30d": "30 days", "90d": "90 days", "1y": "Year", all: "All time" };

function fillTimeline(data) {
  if (data.granularity !== "day" || !data.timeline.length) return data.timeline;
  const days = { "7d": 7, "30d": 30, "90d": 90 }[data.range];
  const map = Object.fromEntries(data.timeline.map((d) => [d.date, d.plays]));
  const out = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
    out.push({ date: d, plays: map[d] || 0 });
  }
  return out;
}

const fmtLabel = (d, g) => {
  const date = new Date(g === "day" ? `${d}T00:00:00Z` : `${d}-01T00:00:00Z`);
  return date.toLocaleDateString(undefined, g === "day" ? { month: "short", day: "numeric", timeZone: "UTC" } : { month: "short", year: "2-digit", timeZone: "UTC" });
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-zinc-200 bg-white px-6 py-5">
    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
    <p className="mt-2 font-heading text-4xl font-bold tracking-tight" data-testid={testid}>{value}</p>
  </div>
);

const RankList = ({ title, rows, render, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.plays));
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-6" data-testid={testid}>
      <h2 className="mb-4 text-lg font-medium">{title}</h2>
      {rows.length === 0 ? <p className="text-sm text-zinc-500">Nothing yet.</p> : (
        <ol className="space-y-3">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-3" data-testid={`${testid}-row-${i}`}>
              <span className="w-5 font-mono text-xs text-zinc-400">{i + 1}</span>
              {render(r)}
              <div className="hidden w-24 sm:block">
                <div className="h-1.5 rounded-full bg-amber-500 transition-[width] duration-500" style={{ width: `${(r.plays / max) * 100}%` }} />
              </div>
              <span className="w-12 text-right font-mono text-xs text-zinc-500">{r.plays}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default function Stats({ embedded = false }) {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const p = usePlayer();
  useEffect(() => { setData(null); api.get("/stats", { params: { range } }).then((r) => setData(r.data)); }, [range]);

  const playTop = async () => {
    const ids = new Set(data.tracks.map((t) => t.track_id));
    const { data: res } = await api.get("/tracks", { params: { sort: "plays", limit: 100 } });
    const order = data.tracks.map((t) => res.items.find((x) => x.id === t.track_id)).filter(Boolean);
    p.playTracks(order.length ? order : res.items.filter((x) => ids.has(x.id)));
  };

  const ranges = (
    <div className="flex rounded-full border border-zinc-200 bg-white p-1" data-testid="stats-ranges">
      {Object.entries(RANGES).map(([k, v]) => (
        <button key={k} onClick={() => setRange(k)} data-testid={`stats-range-${k}`}
          className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${range === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}>{v}</button>
      ))}
    </div>
  );
  return (
    <div>
      {embedded ? <div className="mb-6 flex justify-end">{ranges}</div> : (
        <PageHeader eyebrow="Your listening" title="Stats" testid="stats-title">{ranges}</PageHeader>
      )}
      {!data ? <Loader2 className="animate-spin text-zinc-400" /> : data.totals.plays === 0 ? (
        <EmptyState icon={BarChart3} title="No plays in this period" text="Press play on anything in your library. Every play is counted here — artists, albums, genres and listening time." />
      ) : (
        <div className="animate-rise space-y-8">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Plays" value={data.totals.plays} testid="stats-total-plays" />
            <Stat label="Hours" value={(data.totals.minutes / 60).toFixed(1)} testid="stats-total-hours" />
            <Stat label="Artists" value={data.totals.artists} testid="stats-total-artists" />
            <Stat label="Songs" value={data.totals.tracks} testid="stats-total-tracks" />
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-6" data-testid="stats-timeline">
            <h2 className="mb-6 text-lg font-medium">Plays over time</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={fillTimeline(data)} margin={{ left: -20, right: 8 }}>
                <XAxis dataKey="date" tickFormatter={(d) => fmtLabel(d, data.granularity)} tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#A1A1AA" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#F4F4F5" }} labelFormatter={(d) => fmtLabel(d, data.granularity)} contentStyle={{ borderRadius: 8, border: "1px solid #E4E4E7", fontSize: 12 }} />
                <Bar dataKey="plays" fill="#D97706" radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid gap-8 lg:grid-cols-2">
            <RankList title="Top artists" rows={data.artists} testid="stats-top-artists" render={(r) => (
              <Link to={`/artist?name=${encodeURIComponent(r.name)}`} className="flex min-w-0 flex-1 items-center gap-3 hover:underline">
                <Cover albumId={r.album_id} hasCover={r.has_cover} rounded="rounded-full" className="h-9 w-9 shrink-0" iconSize={14} />
                <span className="truncate text-sm">{r.name}</span>
              </Link>
            )} />
            <RankList title="Top albums" rows={data.albums} testid="stats-top-albums" render={(r) => (
              <Link to={`/album/${r.album_id}`} className="flex min-w-0 flex-1 items-center gap-3 hover:underline">
                <Cover albumId={r.album_id} hasCover={r.has_cover} className="h-9 w-9 shrink-0" iconSize={14} />
                <span className="min-w-0"><span className="block truncate text-sm">{r.name}</span><span className="block truncate text-xs text-zinc-500">{r.artist}</span></span>
              </Link>
            )} />
            <RankList title="Top genres" rows={data.genres} testid="stats-top-genres" render={(r) => <span className="flex-1 truncate text-sm">{r.name}</span>} />
            <div>
              <RankList title="Top songs" rows={data.tracks} testid="stats-top-tracks" render={(r) => (
                <span className="flex min-w-0 flex-1 items-center gap-3">
                  <Cover albumId={r.album_id} hasCover={r.has_cover} className="h-9 w-9 shrink-0" iconSize={14} />
                  <span className="min-w-0"><span className="block truncate text-sm">{r.title}</span><span className="block truncate text-xs text-zinc-500">{r.artist}</span></span>
                </span>
              )} />
              {data.tracks.length > 0 && (
                <button onClick={playTop} data-testid="stats-play-top-button" className="mt-4 flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm text-white transition-transform hover:scale-[1.03]">
                  <Play size={14} fill="currentColor" />Play top songs
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
